import { OverlayProvider } from '@react-aria/overlays';
import { useOverlayTriggerState } from '@react-stately/overlays';

import type { Meta, StoryFn } from '@storybook/react';
import type { PageOverlayProps } from './PageOverlay';

import { PageOverlay } from './PageOverlay';

import { Button } from 'components/Buttons';
import { ButtonGroup } from 'components/ButtonGroup';
import { InputField } from 'components/InputField';
import { GridRow } from 'components/GridRow';
import { Repeater } from 'components/Repeater';

const codeString = `
  <PageOverlay isOpen={isOpen} title={title} onClose={onCloseFunction}
    onSubmit={onSubmitFunction} buttons={buttons}> {children} </PageOverlay>
`;

// eslint-disable-next-line import/no-default-export
export default {
  title: 'Molecules/PageOverlay',
  component: PageOverlay,
  argTypes: {
    isOpen: {
      table: {
        disable: true,
      },
    },
    noValidate: {
      control: 'boolean',
    },
    onSubmit: {
      table: {
        disable: true,
      },
    },
    onClose: {
      table: {
        disable: true,
      },
    },
    buttons: {
      table: {
        disable: true,
      },
    },
  },
  parameters: {
    inspectComponent: PageOverlay,
    codeString: codeString,
  },
} as Meta<PageOverlayProps>;

const Template: StoryFn<PageOverlayProps> = (args: PageOverlayProps) => {
  const { open, close, isOpen } = useOverlayTriggerState({});
  return (
    <OverlayProvider>
      <Button variant="primary" onClick={() => open()}>
        Open PageOverlay
      </Button>
      <PageOverlay
        {...args}
        isOpen={isOpen}
        buttons={
          <ButtonGroup reversed>
            <Button variant="primary" onClick={() => close()}>
              OK
            </Button>
            <Button variant="secondary" onClick={() => close()}>
              Cancel
            </Button>
          </ButtonGroup>
        }
        onClose={() => close()}
      >
        Your content goes here
      </PageOverlay>
    </OverlayProvider>
  );
};

const defaultArgs: Partial<PageOverlayProps> = {
  title: 'Example Title',
  closeButtonAriaValueText: 'Close',
  noValidate: false,
};

export const PageOverlayExample = Template.bind({});
PageOverlayExample.args = {
  ...defaultArgs,
};

export const Loading = Template.bind({});
Loading.args = {
  ...defaultArgs,
  isLoading: true,
  spinnerAriaValueText: 'Loading',
};

const LongContentTemplate: StoryFn<PageOverlayProps> = (args: PageOverlayProps) => {
  const { open, close, isOpen } = useOverlayTriggerState({});
  return (
    <OverlayProvider>
      <Button variant="primary" onClick={() => open()}>
        Open PageOverlay
      </Button>
      <PageOverlay
        {...args}
        isOpen={isOpen}
        buttons={
          <GridRow>
            <GridRow.Column size="8" mediumSize="9" startPositon="2" mediumStartPosition="3">
              <ButtonGroup reversed>
                <Button variant="primary" onClick={() => close()}>
                  OK
                </Button>
                <Button variant="secondary" onClick={() => close()}>
                  Cancel
                </Button>
              </ButtonGroup>
            </GridRow.Column>
          </GridRow>
        }
        onClose={() => close()}
      >
        <GridRow marginTop="100">
          <GridRow.Column size="8" mediumSize="9" startPositon="2" mediumStartPosition="3">
            <Repeater gap="100">
              <div>
                Lorem ipsum dolor sit amet, consectetur adipiscing elit. Ut ac dui et nibh eleifend ultricies a id
                nulla. Pellentesque eleifend sem a tellus dictum tempus. Proin non leo id risus placerat vulputate at ac
                erat. Ut fringilla imperdiet sollicitudin. Vestibulum ante ipsum primis in faucibus orci luctus et
                ultrices posuere cubilia curae; Gijs molestie ante ac magna ornare rhoncus non at quam. Mauris elementum
                luctus euismod. Pellentesque molestie malesuada purus, sed egestas lacus interdum sit amet. Morbi
                blandit lacus sit amet dictum pulvinar. Ut semper nunc ac enim imperdiet, non interdum diam gravida. In
                nunc tortor, mollis ac molestie dignissim, elementum sed quam. Suspendisse mollis venenatis Laura,
                pellentesque vestibulum elit consequat et. Aliquam fringilla tellus id libero tempus, sit amet suscipit
                augue tristique. Quisque cursus neque vel justo tristique, eget semper turpis elementum. Etiam venenatis
                mattis sapien ac mattis. Mauris vitae ultrices erat.
              </div>
              <div>
                Aliquam facilisis massa sed justo rhoncus bibendum. Sed lobortis urna orci, vitae porta leo placerat
                nec. Nam porta porttitor ornare. Suspendisse vehicula nunc nec egestas finibus. Aliquam non luctus
                risus. Pien ac diam tincidunt, bibendum sem eget, sollicitudin neque. Curabitur mauris risus, feugiat ac
                euismod non, vulputate ac justo. Curabitur pretium vehicula augue, blandit porta odio rutrum sit amet.
                Proin semper enim ut sapien tempor, a ornare ipsum faucibus.
              </div>
              <Button variant="primary">Test Button</Button>
              <div>
                Cras bibendum arcu vitae nulla venenatis, at cursus urna dapibus. In hac habitasse platea dictumst. In
                porttitor at lectus nec rhoncus. In hac habitasse platea dictumst. Sed nec lectus neque. Aliquam erat
                volutpat. Morbi elit orci, ultricies a porttitor eget, eleifend eu metus. Maarten sollicitudin vitae
                massa a tempor. Aliquam dignissim metus id sem sollicitudin, eget lacinia nulla imperdiet. Nullam nec
                vestibulum eros, ut aliquam libero. Nulla hendrerit cursus ante id auctor. Curabitur condimentum
                malesuada maximus. Aliquam felis diam, gravida nec eleifend quis, placerat et orci.
              </div>
              <div>
                Nulla leo ligula, hendrerit vel egestas in, suscipit vitae erat. Vivamus interdum quam lacinia, egestas
                felis non, volutpat velit. Vestibulum orci diam, tincidunt ac mattis quis, ornare posuere magna. Aliquam
                erat volutpat. Maecenas facilisis lacus id ante efficitur, non dignissim lorem suscipit. Praesent at
                posuere velit. Nulla imperdiet, dui rhoncus volutpat feugiat, eros mauris cursus justo, a tincidunt
                ipsum ipsum eu nisl. Nulla eu dui Thierry. Aliquam vitae risus ut velit fringilla sodales nec id lacus.
                Interdum et malesuada fames ac ante ipsum primis in faucibus. Suspendisse ullamcorper purus quis nunc
                placerat, vel bibendum urna mollis. Nulla facilisi. Phasellus sed erat nec ex ullamcorper viverra.
                Phasellus convallis nibh sed dolor vulputate convallis. Phasellus ac posuere sapien, et elementum ipsum.
              </div>
              <InputField id="test-input" placeholder="Test Input" />
              <div>
                Sed malesuada ut nulla ut maximus. Class aptent taciti sociosqu ad litora torquent per conubia nostra,
                per inceptos himenaeos. Morten in libero rhoncus, maximus tortor sit amet, molestie ex. Mauris rhoncus
                et erat dapibus aliquam. Vestibulum placerat quam eget lorem tempor, vel venenatis lorem malesuada. Duis
                ut venenatis arcu. Cras lacinia mi ac dui sollicitudin semper sit amet eget nisl. Fusce viverra ex eget
                tortor molestie dictum. Cras ullamcorper faucibus nisi in congue. Sanne in tristique est. Praesent id
                rhoncus ligula, ut porta dui. Sed tincidunt, mi nec varius auctor, nunc diam venenatis felis, at
                facilisis tortor sem et magna. Aliquam erat volutpat.
              </div>
              <div>
                Proin ipsum ante, auctor id consectetur a, pulvinar id nibh. Phasellus molestie quam eu mi lacinia
                condimentum. Nullam fermentum id diam eu pharetra. Donec dictum sem vitae Jos scelerisque semper.
                Suspendisse sed facilisis orci. Sed ut scelerisque odio. Curabitur felis felis, finibus ac orci at,
                pellentesque efficitur risus. Sed et leo ac orci mollis tincidunt et pulvinar velit. Duis non nibh
                tincidunt, lacinia urna a, fringilla augue. Benjamin fermentum, massa a mollis cursus, ex dui tincidunt
                tellus, ut sollicitudin ex neque ac sapien.
              </div>
              <div>
                Nunc rhoncus imperdiet leo. Fusce interdum malesuada aliquet. Aenean porttitor, tortor et volutpat
                blandit, sapien diam scelerisque massa, ac facilisis metus turpis id quam. Donec ac elementum augue,
                congue pulvinar arcu. Interdum et malesuada fames ac ante ipsum primis in faucibus. Cras tempor
                convallis elit, id vehicula Robin semper nec. Sed ut ultrices nulla. Cras nec dolor justo. Proin mollis
                mauris ligula, luctus faucibus dolor ultrices ac. Nunc quis mauris fringilla, pellentesque massa nec,
                dictum metus. Praesent odio ligula, tincidunt ultrices maximus quis, accumsan ut odio. Nullam iaculis
                molestie nulla quis ultrices. Praesent quam velit, sagittis eget auctor a, dignissim ut massa. Donec
                mollis at lorem sit amet accumsan. Praesent nec nulla laoreet nunc pretium egestas. Malesuada Wim arcu
                pharetra, dictum nibh a, facilisis risus.
              </div>
            </Repeater>
          </GridRow.Column>
        </GridRow>
      </PageOverlay>
    </OverlayProvider>
  );
};

export const WithLongComplexContent = LongContentTemplate.bind({});
WithLongComplexContent.args = {
  ...defaultArgs,
};
